import React, { Component } from 'react';
import { connect } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { DROPDOWN_CONFIG, DATATABLE_CONFIG, USER_SUBMENU_ID } from '../../../shared/Config';
import { HASACCESS } from '../../../shared/utility';
import Messages from '../../../shared/UI/Messages/Messages';
import * as actions from '../../../store/actions/index';

// Sections shown per date, each backed by a single { Menuitem, Qty, Unit, Value } record
// and rendered as 3 child columns: Qty / Unit / Value.
// Keys match the backend row shape (P / PK / W / S). Displayed directly — no expand/collapse.
const SECTIONS = [
    { key: 'P',  label: 'Planned Menuitem' },
    { key: 'PK', label: 'Production Menuitem' },
    { key: 'W',  label: 'Wastage Menuitem' },
    { key: 'S',  label: 'Soldout Menu Item' },
];

// Dummy data — replace with API/Redux data
function seedForDate(dateStr, sectionIndex) {
    // deterministic-ish pseudo values so Search/Show All show something meaningful
    const base = (dateStr.split('-').reduce((a, c) => a + Number(c), 0) + sectionIndex * 7) % 23;
    return {
        Menuitem: `Item ${sectionIndex + 1}`,
        Qty: base + 1,
        Unit: 'Kg',
        Value: (base + 1) * 10,
    };
}

// Received Amount is an exception — a single amount, not a Qty/Unit/Value triplet.
function seedReceivedAmount(dateStr) {
    const base = (dateStr.split('-').reduce((a, c) => a + Number(c), 0)) % 23;
    return { Value: (base + 1) * 15 };
}

// Issued Ingredient is the other exception — multiple records per date.
// Collapsed: shows only the total Qty/Value. Expanded: shows each record.
function seedIngredients(dateStr) {
    const count = (dateStr.split('-').reduce((a, c) => a + Number(c), 0) % 3) + 1;
    return Array.from({ length: count }, (_, i) => ({
        IngredientName: `Ingredient ${i + 1}`,
        Qty: (i + 1) * 2,
        Unit: 'Kg',
        Value: (i + 1) * 25,
    }));
}

// Sum of the individual Ingredient records' Value — shown in the collapsed row.
function totalIngredientValue(ingredientList) {
    return (ingredientList || []).reduce((acc, ing) => acc + Number(ing.Value || 0), 0);
}

function generateDateRange(from, to) {
    const dates = [];
    const cur = new Date(from);
    const end = new Date(to);
    while (cur <= end) {
        dates.push(new Date(cur));
        cur.setDate(cur.getDate() + 1);
    }
    return dates;
}

function toKey(date) {
    return date.toISOString().split('T')[0];
}

function formatHeaderDate(date) {
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

class DailySalesReport extends Component {
    state = {
        Restaurant: null,
        fromDate: new Date(),
        toDate: new Date(),
        tableData: [],
        searched: false,
        expandedRows: null,
    };

    componentDidMount() {
        this.getAccess = HASACCESS(USER_SUBMENU_ID);
    }

    buildTableData = (dates) => {
        return dates.map(date => {
            const dateStr = toKey(date);
            const row = {
                id: dateStr,
                date,
                dateStr,
            };
            SECTIONS.forEach((section, idx) => {
                row[section.key] = seedForDate(dateStr, idx);
            });
            row.Ingredient = seedIngredients(dateStr);
            row.R = seedReceivedAmount(dateStr);
            return row;
        });
    };

    handleSearch = () => {
        const { fromDate, toDate } = this.state;
        if (!fromDate || !toDate) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'Please select From Date and To Date' });
            return;
        }
        if (fromDate > toDate) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'From Date cannot be after To Date' });
            return;
        }
        const dates     = generateDateRange(fromDate, toDate);
        const tableData = this.buildTableData(dates);
        this.setState({ tableData, searched: true });
    };

    handleShowAll = () => {
        const to   = new Date();
        const from = new Date();
        from.setDate(from.getDate() - 3); // sample: today + 3 preceding days
        const dates     = generateDateRange(from, to);
        const tableData = this.buildTableData(dates);
        this.setState({ fromDate: from, toDate: to, tableData, searched: true });
    };

    // Flat row per date for export — Ingredient contributes only its total Value,
    // never the individual records (those are UI-only, shown on expand).
    //
    // Mirrors the on-screen DataTable's grouped header: Date | section (Qty/Unit/Value) x4
    // | Issued Ingredient (total only) | Received Amount. Shared by both Excel and PDF export
    // so their layout stays identical to what's rendered on screen.
    buildExportMatrix = () => {
        const { tableData } = this.state;

        // Row 1 = group labels (colSpan shown via repeated '' placeholders for merge/skip),
        // Row 2 = Qty/Unit/Value sub-labels.
        const headerRow1 = ['Date'];
        const headerRow2 = [''];
        SECTIONS.forEach(section => {
            headerRow1.push(section.label, '', '');
            headerRow2.push('Qty', 'Unit', 'Value');
        });
        headerRow1.splice(4, 0, 'Issued Ingredient');
        headerRow2.splice(4, 0, '');
        headerRow1.push('Received Amount');
        headerRow2.push('');

        const body = tableData.map(row => {
            const line = [formatHeaderDate(row.date)];
            SECTIONS.forEach((section, idx) => {
                const values = [row[section.key].Qty, row[section.key].Unit, row[section.key].Value];
                if (idx === 0) {
                    line.push(...values, totalIngredientValue(row.Ingredient));
                } else {
                    line.push(...values);
                }
            });
            line.push(row.R.Value);
            return line;
        });

        return { headerRow1, headerRow2, body };
    };

    handleExportExcel = () => {
        const { headerRow1, headerRow2, body } = this.buildExportMatrix();
        if (body.length === 0) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'No data to export' });
            return;
        }

        const worksheet = XLSX.utils.aoa_to_sheet([headerRow1, headerRow2, ...body]);
        worksheet['!cols'] = headerRow1.map(() => ({ wch: 16 }));

        // Column indexes: 0 Date | 1-3 Planned | 4 Ingredient | 5-7 Production
        // | 8-10 Wastage | 11-13 Soldout | 14 Received Amount
        worksheet['!merges'] = [
            { s: { r: 0, c: 0 },  e: { r: 1, c: 0 } },  // Date
            { s: { r: 0, c: 1 },  e: { r: 0, c: 3 } },  // Planned Menuitem
            { s: { r: 0, c: 4 },  e: { r: 1, c: 4 } },  // Issued Ingredient
            { s: { r: 0, c: 5 },  e: { r: 0, c: 7 } },  // Production Menuitem
            { s: { r: 0, c: 8 },  e: { r: 0, c: 10 } }, // Wastage Menuitem
            { s: { r: 0, c: 11 }, e: { r: 0, c: 13 } }, // Soldout Menu Item
            { s: { r: 0, c: 14 }, e: { r: 1, c: 14 } }, // Received Amount
        ];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Daily Sales Report');

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(blob, 'Daily_Sales_Report.xlsx');
    };

    handleExportPdf = () => {
        const { body } = this.buildExportMatrix();
        if (body.length === 0) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'No data to export' });
            return;
        }

        const doc = new jsPDF('l', 'mm', 'a4');
        doc.setFontSize(12);
        doc.text('Daily Sales Report', 14, 10);
        doc.autoTable({
            head: [
                [
                    { content: 'Date', rowSpan: 2 },
                    { content: SECTIONS[0].label, colSpan: 3, styles: { halign: 'center' } },
                    { content: 'Issued Ingredient', rowSpan: 2 },
                    { content: SECTIONS[1].label, colSpan: 3, styles: { halign: 'center' } },
                    { content: SECTIONS[2].label, colSpan: 3, styles: { halign: 'center' } },
                    { content: SECTIONS[3].label, colSpan: 3, styles: { halign: 'center' } },
                    { content: 'Received Amount', rowSpan: 2 },
                ],
                ['Qty', 'Unit', 'Value', 'Qty', 'Unit', 'Value', 'Qty', 'Unit', 'Value', 'Qty', 'Unit', 'Value'],
            ],
            body,
            startY: 15,
            styles: { fontSize: 7, cellPadding: 1.5, halign: 'center' },
            headStyles: { fillColor: [41, 128, 185], halign: 'center' },
        });
        doc.save('Daily_Sales_Report.pdf');
    };

    dateBodyTemplate = (rowData) => formatHeaderDate(rowData.date);

    qtyBodyTemplate = (section) => (rowData) => rowData[section].Qty;

    unitBodyTemplate = (section) => (rowData) => rowData[section].Unit;

    valueBodyTemplate = (section) => (rowData) => `₹ ${Number(rowData[section].Value).toLocaleString('en-IN')}`;

    // Received Amount — single value column, no Qty/Unit split.
    receivedAmountBodyTemplate = (rowData) => `₹ ${Number(rowData.R.Value).toLocaleString('en-IN')}`;

    // Collapsed row shows only the total Ingredient Value.
    ingredientTotalValueBodyTemplate = (rowData) => `₹ ${totalIngredientValue(rowData.Ingredient).toLocaleString('en-IN')}`;

    // Expanded row shows the individual Ingredient records only — no other section is affected.
    // Offset = Date + Planned Menuitem (Qty/Unit/Value) + the expander column widths,
    // so the panel lines up directly under the "Issued Ingredient" column.
    ingredientRowExpansionTemplate = (rowData) => (
        <div className="p-p-3" style={{ display: 'flex', justifyContent: 'flex-start', marginLeft: 'calc(150px + 90px + 90px + 120px + 3em)' }}>
            <DataTable value={rowData.Ingredient} dataKey="IngredientName" style={{ width: 'auto' }} {...DATATABLE_CONFIG}>
                <Column field="IngredientName" header="Ingredient Name" style={{ width: '1%', whiteSpace: 'nowrap' }} />
                <Column field="Qty" header="Qty" style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'center' }} />
                <Column field="Unit" header="Unit" style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'center' }} />
                <Column field="Value" header="Value" body={(d) => `₹ ${Number(d.Value).toLocaleString('en-IN')}`} style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'center' }} />
            </DataTable>
        </div>
    );

    render() {
        if (this.getAccess !== undefined && this.getAccess.viewAccess === false) {
            this.props.history.push('/dashboard');
        }

        const { tableData, fromDate, toDate, searched } = this.state;

        // ── Grouped header ──────────────────────────────────────────────
        // Column order: Date | Planned Menuitem | Issued Ingredient | Production Menuitem
        //             | Wastage Menuitem | Soldout Menu Item | Received Amount
        // Row 1: group headers. Row 2: Qty/Unit/Value leaves (sections) —
        // Date, Issued Ingredient (total Value only) and Received Amount span both rows.
        const headerGroup = (
            <ColumnGroup>
                <Row>
                    <Column
                        header="Date"
                        rowSpan={2}
                        style={{ minWidth: '150px' }}
                    />

                    {/* Planned Menuitem */}
                    <Column header={SECTIONS[0].label} colSpan={3} style={{ textAlign: 'center', borderLeft: '1px solid #dee2e6' }} />

                    {/* Issued Ingredient — exception: expander + total Value only (1 leaf, not 3) */}
                    <Column header="" rowSpan={2} style={{ width: '3em', borderLeft: '1px solid #dee2e6' }} />
                    <Column
                        header="Issued Ingredient"
                        rowSpan={2}
                        style={{ minWidth: '120px', textAlign: 'center' }}
                    />

                    {/* Production Menuitem */}
                    <Column header={SECTIONS[1].label} colSpan={3} style={{ textAlign: 'center', borderLeft: '1px solid #dee2e6' }} />

                    {/* Wastage Menuitem */}
                    <Column header={SECTIONS[2].label} colSpan={3} style={{ textAlign: 'center', borderLeft: '1px solid #dee2e6' }} />

                    {/* Soldout Menu Item */}
                    <Column header={SECTIONS[3].label} colSpan={3} style={{ textAlign: 'center', borderLeft: '1px solid #dee2e6' }} />

                    {/* Received Amount — exception: single value, no children */}
                    <Column
                        header="Received Amount"
                        rowSpan={2}
                        style={{ minWidth: '130px', textAlign: 'center', borderLeft: '1px solid #dee2e6' }}
                    />
                </Row>
                <Row>
                    {/* Planned Menuitem leaves */}
                    <Column header="Qty"   style={{ minWidth: '90px', textAlign: 'center', borderLeft: '1px solid #dee2e6' }} />
                    <Column header="Unit"  style={{ minWidth: '90px', textAlign: 'center' }} />
                    <Column header="Value" style={{ minWidth: '120px', textAlign: 'center' }} />

                    {/* Production Menuitem leaves */}
                    <Column header="Qty"   style={{ minWidth: '90px', textAlign: 'center', borderLeft: '1px solid #dee2e6' }} />
                    <Column header="Unit"  style={{ minWidth: '90px', textAlign: 'center' }} />
                    <Column header="Value" style={{ minWidth: '120px', textAlign: 'center' }} />

                    {/* Wastage Menuitem leaves */}
                    <Column header="Qty"   style={{ minWidth: '90px', textAlign: 'center', borderLeft: '1px solid #dee2e6' }} />
                    <Column header="Unit"  style={{ minWidth: '90px', textAlign: 'center' }} />
                    <Column header="Value" style={{ minWidth: '120px', textAlign: 'center' }} />

                    {/* Soldout Menu Item leaves */}
                    <Column header="Qty"   style={{ minWidth: '90px', textAlign: 'center', borderLeft: '1px solid #dee2e6' }} />
                    <Column header="Unit"  style={{ minWidth: '90px', textAlign: 'center' }} />
                    <Column header="Value" style={{ minWidth: '120px', textAlign: 'center' }} />
                </Row>
            </ColumnGroup>
        );

        return (
            <div className="p-grid padding">
                {this.props.loading  && <div className="page-loader"></div>}
                {this.props.error   !== null && <Messages type="error"   message={this.props.error} />}
                {this.props.success !== null && <Messages type="success" message={this.props.success} />}
                <Toast ref={(el) => this.growl = el} />

                <div className="p-col-12">
                    <div className="card card-w-title" style={{ position: 'relative' }}>
                        <h1>Daily Sales Report</h1>

                        {/* ── Filter row ───────────────────────────────────── */}
                        <div className="p-grid p-align-end">

                            <div className="p-col-12 p-md-2 p-lg-2 p-d-flex p-flex-column p-gap-2">
                                <label className="label">Restaurant</label>
                                <Dropdown
                                    placeholder="Restaurant"
                                    name="Restaurant"
                                    options={[
                                        { label: 'Select Category', value: null },
                                        { label: 'Category 1', value: 'cat1' },
                                        { label: 'Category 2', value: 'cat2' },
                                    ]}
                                    value={this.state.Restaurant || null}
                                    onChange={(e) => this.setState({ Restaurant: e.value })}
                                    filter
                                    autoWidth
                                    className="p-col-12"
                                    {...DROPDOWN_CONFIG}
                                />
                            </div>

                            <div className="p-col-12 p-md-2 p-lg-2 p-d-flex p-flex-column p-gap-2">
                                <label className="label">From Date</label>
                                <Calendar
                                    placeholder="From Date"
                                    value={fromDate}
                                    onChange={(e) => this.setState({ fromDate: e.value })}
                                />
                            </div>

                            <div className="p-col-12 p-md-2 p-lg-2 p-d-flex p-flex-column p-gap-2">
                                <label className="label">To Date</label>
                                <Calendar
                                    placeholder="To Date"
                                    value={toDate}
                                    onChange={(e) => this.setState({ toDate: e.value })}
                                />
                            </div>

                            {/* Search + Show All — inline with filters */}
                            <div className="p-col-12 p-md-4 p-lg-4 p-d-flex p-ai-end p-gap-2">
                                <Button
                                    label="Search"
                                    className="p-button-primary"
                                    onClick={this.handleSearch}
                                />
                                <Button
                                    label="Show All"
                                    className="p-button-secondary p-ml-2"
                                    onClick={this.handleShowAll}
                                />
                            </div>

                            {/* Export — own full-width row, pushed to the right */}
                            <div className="p-col-12 p-d-flex p-jc-end p-gap-2 p-mt-1">
                                <Button
                                    label="Export as Excel"
                                    className="p-button-warning"
                                    onClick={this.handleExportExcel}
                                    disabled={!searched}
                                />
                                <Button
                                    label="Export as PDF"
                                    className="p-button-danger p-ml-2"
                                    onClick={this.handleExportPdf}
                                    disabled={!searched}
                                />
                            </div>

                        </div>
                        {/* ── End filter row ───────────────────────────────── */}

                        {/* ── DataTable ────────────────────────────────────── */}
                        {searched && (
                            <div className="p-col-12" style={{ marginTop: '1em', overflowX: 'auto' }}>
                                <DataTable
                                    value={tableData}
                                    dataKey="id"
                                    headerColumnGroup={headerGroup}
                                    scrollable
                                    scrollHeight="500px"
                                    scrollDirection="both"
                                    style={{ width: '100%' }}
                                    expandedRows={this.state.expandedRows}
                                    onRowToggle={(e) => this.setState({ expandedRows: e.data })}
                                    rowExpansionTemplate={this.ingredientRowExpansionTemplate}
                                    {...DATATABLE_CONFIG}
                                >
                                    <Column
                                        field="dateStr"
                                        body={this.dateBodyTemplate}
                                        style={{ minWidth: '150px' }}
                                    />

                                    {/* Planned Menuitem (P) */}
                                    <Column field="P.Qty"   body={this.qtyBodyTemplate('P')}   style={{ minWidth: '90px', textAlign: 'center', borderLeft: '1px solid #dee2e6' }} />
                                    <Column field="P.Unit"  body={this.unitBodyTemplate('P')}  style={{ minWidth: '90px', textAlign: 'center' }} />
                                    <Column field="P.Value" body={this.valueBodyTemplate('P')} style={{ minWidth: '120px', textAlign: 'center' }} />

                                    {/* Issued Ingredient — expander toggles only this section's detail panel */}
                                    <Column
                                        expander
                                        style={{ width: '3em', borderLeft: '1px solid #dee2e6' }}
                                    />
                                    <Column body={this.ingredientTotalValueBodyTemplate} style={{ minWidth: '120px', textAlign: 'center' }} />

                                    {/* Production Menuitem (PK) */}
                                    <Column field="PK.Qty"   body={this.qtyBodyTemplate('PK')}   style={{ minWidth: '90px', textAlign: 'center', borderLeft: '1px solid #dee2e6' }} />
                                    <Column field="PK.Unit"  body={this.unitBodyTemplate('PK')}  style={{ minWidth: '90px', textAlign: 'center' }} />
                                    <Column field="PK.Value" body={this.valueBodyTemplate('PK')} style={{ minWidth: '120px', textAlign: 'center' }} />

                                    {/* Wastage Menuitem (W) */}
                                    <Column field="W.Qty"   body={this.qtyBodyTemplate('W')}   style={{ minWidth: '90px', textAlign: 'center', borderLeft: '1px solid #dee2e6' }} />
                                    <Column field="W.Unit"  body={this.unitBodyTemplate('W')}  style={{ minWidth: '90px', textAlign: 'center' }} />
                                    <Column field="W.Value" body={this.valueBodyTemplate('W')} style={{ minWidth: '120px', textAlign: 'center' }} />

                                    {/* Soldout Menu Item (S) */}
                                    <Column field="S.Qty"   body={this.qtyBodyTemplate('S')}   style={{ minWidth: '90px', textAlign: 'center', borderLeft: '1px solid #dee2e6' }} />
                                    <Column field="S.Unit"  body={this.unitBodyTemplate('S')}  style={{ minWidth: '90px', textAlign: 'center' }} />
                                    <Column field="S.Value" body={this.valueBodyTemplate('S')} style={{ minWidth: '120px', textAlign: 'center' }} />

                                    {/* Received Amount (R) — single value column */}
                                    <Column
                                        field="R.Value"
                                        body={this.receivedAmountBodyTemplate}
                                        style={{ minWidth: '130px', textAlign: 'center', borderLeft: '1px solid #dee2e6' }}
                                    />
                                </DataTable>
                            </div>
                        )}

                        {/* Empty state */}
                        {!searched && (
                            <div className="p-d-flex p-jc-center p-ai-center" style={{ minHeight: '200px', color: '#aaa', fontSize: '15px' }}>
                                <i className="pi pi-search p-mr-2" />
                                Select a date range and click Search to view the report.
                            </div>
                        )}

                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    loggedUserDetail: state.auth.userObject,
    loading:          state.master.loading,
    error:            state.master.error,
    success:          state.master.success,
    tableLists:       state.master.tableLists,
});

const mapDispatchToProps = dispatch => ({
    dynamicDeleteProcess: (model, data, func) => dispatch(actions.dynamicDeleteProcess(model, data, func)),
    getMasterTableList:   (table)             => dispatch(actions.getMasterTableList(table)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DailySalesReport);
